import { useCallback, useMemo, FC } from "react";
import { NavLink } from "react-router-dom";
import dayjs from "dayjs";
import { formatAnalysisDescription } from "../../../utils/analysis-utils";
import { Field, Form, Formik } from "formik";
import {
  AccessTime,
  Add,
  Cancel,
  CheckCircle,
  Delete,
  Info,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fab,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import AnalysisProcessingDialog from "../../../components/dialog/analysis-processing-dialog.component";
import DialogVersion from "../../../components/dialog/dialog.component";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import { Value } from "../../../components/table/value-row-column.component";
import {
  Analysis,
  AnalysisStatus,
  AnalysisStatusMapper,
} from "../../../models/general/analysis";
import { translate } from "../../../translate/translate";
import { useAnalysisPage } from "./useAnalysisPage";
import Loading from "../../../components/loading/loading";

export interface MaterialStatusBadgeProps {
  row: any;
}

export const MaterialStatusBadge: FC<MaterialStatusBadgeProps> = ({ row }) => {
  const generatedAt = row.material_status_generated_at;
  const parsedDate = generatedAt ? dayjs(generatedAt) : null;
  const isValidDate = Boolean(parsedDate && parsedDate.isValid());

  const tooltipTitle = useMemo(() => {
    if (isValidDate && parsedDate) {
      const timeFormat = translate.language === "pt" ? "HH:mm" : "hh:mm A";
      const formattedDate = parsedDate.format("L");
      const formattedTime = parsedDate.format(timeFormat);
      return translate.t("material_status_generated_at_tooltip", {
        date: `${formattedDate} ${formattedTime}`,
        interpolation: { escapeValue: false },
      });
    }
    return translate.t("material_status_generated_tooltip");
  }, [isValidDate, parsedDate]);

  return (
    <Tooltip
      placement="top"
      title={tooltipTitle}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: "14px",
          },
        },
      }}
    >
      <span
        id={`badge-material-status-${row.id}`}
        style={{
          backgroundColor: "#ECFDF5",
          color: "#047857",
          border: "1px solid #A7F3D0",
          padding: "2px 10px",
          borderRadius: "9999px",
          fontSize: "12px",
          fontWeight: 500,
          display: "inline-block",
          whiteSpace: "nowrap",
          lineHeight: "16px",
        }}
      >
        {translate.t("material_status")}
      </span>
    </Tooltip>
  );
};

export interface AnalysisTypeCellProps {
  row: any;
}

export const AnalysisTypeCell: FC<AnalysisTypeCellProps> = ({ row }) => {
  const isOriginal = row["parent_analysis_id"] === null || row["parent_analysis_id"] === undefined;
  const typeLabel = isOriginal
    ? translate.t("analysis_type_analysis")
    : translate.t("simulation_type_analysis");

  const chipStyle = {
    backgroundColor: isOriginal ? "#E3F2FD" : "#F3E5F5",
    color: isOriginal ? "#1976D2" : "#7B1FA2",
    border: isOriginal ? "1px solid #BBDEFB" : "1px solid #E1BEE7",
    padding: "2px 10px",
    borderRadius: "9999px",
    fontSize: "12px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    whiteSpace: "nowrap" as const,
    width: "fit-content",
    lineHeight: "16px",
  };

  const typeChip = (
    <span style={chipStyle}>
      {typeLabel}
      {!isOriginal && (
        <Info style={{ fontSize: "14px", color: "#7B1FA2" }} />
      )}
    </span>
  );

  const typeChipWithTooltip = !isOriginal ? (
    <Tooltip
      placement="top"
      title={translate.t("simulation_tooltip", { id: row["parent_analysis_id"] })}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: "14px",
          },
        },
      }}
    >
      {typeChip}
    </Tooltip>
  ) : (
    typeChip
  );

  const hasMaterialStatus = Boolean(row.has_material_status);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      flexWrap="wrap"
      gap={0.75}
      width="100%"
    >
      {typeChipWithTooltip}
      {hasMaterialStatus && <MaterialStatusBadge row={row} />}
    </Box>
  );
};

const AnalysisPageComponent: FC = () => {
  const {
    analysis,
    setAnalysis,
    dataSource,
    pagination,
    choices,
    rowsPerPage,
    page,
    searchFilter,
    openDeleteModal,
    openAnalysisProcessingDialog,
    selectedIds,
    toggleSelectOne,
    toggleSelectAll,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRowClick,
    handleCloseDeleteModal,
    handleCloseAnalysisProcessingDialog,
    handleOpenAnalysisProcessingModal,
    handleOpenDeleteModal,
    handleSearchSubmit,
    handleSearchChange,
    percentage,
    DISPLAYED_COLUMNS_SHOW,
    DISPLAYED_COLUMNS,
    SHOW_ACTIONS,
    DATE_COLUMNS,
    STATUS_DOT_COLORS,
    deletingIds,
    confirmDeleteMessage,
    isLoading,
  } = useAnalysisPage();

  const headerCellCustomStyleHandler = useCallback((column: any) => {
    const styles: Record<string, any> = {
      select: { width: "40px", minWidth: "40px", maxWidth: "40px", textAlign: "center" },
      id: { width: "64px", minWidth: "64px", maxWidth: "64px", textAlign: "center" },
      description: { width: "320px", minWidth: "320px" },
      processing: { width: "150px", minWidth: "150px", whiteSpace: "nowrap" },
      type: { width: "230px", minWidth: "230px" },
      creation_date: { width: "160px", minWidth: "160px", whiteSpace: "nowrap" },
      customer: { width: "90px", minWidth: "90px", whiteSpace: "nowrap" },
      user: { width: "130px", minWidth: "130px", whiteSpace: "nowrap" },
      action: { width: "70px", minWidth: "70px", maxWidth: "70px", textAlign: "center" },
    };

    return styles[column] || null;
  }, []);

  const cellCustomStyleHandler = useCallback((column: any) => {
    if (column === "select") {
      return { width: "40px", minWidth: "40px", maxWidth: "40px", textAlign: "center" };
    }
    if (column === "id") {
      return { width: "64px", minWidth: "64px", maxWidth: "64px", textAlign: "center" };
    }
    if (column === "description") {
      return {
        width: "320px",
        minWidth: "320px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        overflowWrap: "break-word",
      };
    }
    if (column === "status" || column === "processing") {
      return { width: "150px", minWidth: "150px", whiteSpace: "nowrap" };
    }
    if (column === "parent_analysis_id" || column === "type") {
      return { width: "230px", minWidth: "230px" };
    }
    if (column === "created_at" || column === "creation_date") {
      return { width: "160px", minWidth: "160px", whiteSpace: "nowrap" };
    }
    if (column === "customer") {
      return {
        width: "90px",
        minWidth: "90px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    }
    if (column === "user_name" || column === "user") {
      return {
        width: "130px",
        minWidth: "130px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    }
    if (column === "action") {
      return { width: "70px", minWidth: "70px", maxWidth: "70px", textAlign: "center" };
    }
    return null;
  }, []);

  const renderCreatedAtCell = useCallback((row: any) => {
    const cleanedDateTime = String(Value(row, "created_at")).replace(/\.\d+/, "");
    const timeFormat = translate.language === "pt" ? "HH:mm" : "hh:mm A";

    const formattedDate = dayjs(cleanedDateTime).format("L");
    const formattedTime = dayjs(cleanedDateTime).format(timeFormat);

    return `${formattedDate} - ${formattedTime}`;
  }, []);

  const renderTypeCell = useCallback((row: any) => {
    return <AnalysisTypeCell row={row} />;
  }, []);

  const renderStatusCell = useCallback(
    (row: any) => {
      const choice = choices.find((choice: any) => choice.value === row["status"]);
      let statusText = AnalysisStatusMapper[choice?.value as AnalysisStatus];
      let tooltip = "";

      const isProcessing = choice?.value === AnalysisStatus.PROCESSING;
      const isWaiting = choice?.value === AnalysisStatus.WAITING;
      const isCompleted = choice?.value === AnalysisStatus.COMPLETED;
      const isError = choice?.value === AnalysisStatus.ERROR;
      const isCanceled = choice?.value === AnalysisStatus.CANCELED;
      const isSaveError = choice?.value === AnalysisStatus.SAVE_ERROR;
      const isFullyCompleted = row["is_fully_completed"] === true;

      if (isWaiting) {
        tooltip = translate.t("waiting_analysis_tooltip");
      } else if (isProcessing) {
        tooltip = translate.t("processing_analysis_tooltip");
      } else if (isError) {
        tooltip = translate.t("calculation_error_analysis_tooltip");
      } else if (isSaveError) {
        tooltip = translate.t("save_error_analysis_tooltip");
      } else if (isCompleted && !isFullyCompleted) {
        statusText = translate.t("finalizing_analysis");
        tooltip = translate.t("finalizing_analysis_tooltip");
      } else if (isCompleted && isFullyCompleted) {
        statusText = translate.t("concluded_analysis");
        tooltip = translate.t("concluded_analysis_tooltip");
      }

      const fontColor = (isCompleted && isFullyCompleted) ? "#90a4ae" : "#45485f";
      const dotColor = STATUS_DOT_COLORS[choice?.value] || "#BDBDBD";

      let statusIndicator;

      if (isProcessing || (isCompleted && !isFullyCompleted)) {
        statusIndicator = (
          <AccessTime
            style={{ cursor: "pointer", width: 14, height: 14, color: "#f8b653" }}
            id="ic-processing-analysis"
          />
        );
      } else if (isCompleted && isFullyCompleted) {
        statusIndicator = (
          <CheckCircle
            style={{ cursor: "pointer", width: 14, height: 14, color: "#4CAF4F" }}
            id="ic-completed-analysis"
          />
        );
      } else if (isWaiting) {
        statusIndicator = (
          <AccessTime
            style={{ cursor: "pointer", width: 14, height: 14, color: "#3949ab" }}
            id="ic-waiting-analysis"
          />
        );
      } else if (isError || isCanceled || isSaveError) {
        statusIndicator = (
          <Cancel
            style={{ cursor: "pointer", width: 14, height: 14, color: "#f44336" }}
            id="ic-error-analysis"
          />
        );
      } else {
        statusIndicator = (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: dotColor,
            }}
          />
        );
      }

      const content = (
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ width: 14, height: 14 }}
          >
            {statusIndicator}
          </Box>
          <Typography sx={{ color: fontColor, fontSize: "14px" }}>
            {statusText}
          </Typography>
          {isProcessing && (
            <Typography variant="body1" sx={{ fontSize: "14px" }}>({percentage(row)})</Typography>
          )}
        </Box>
      );

      return tooltip ? (
        <Tooltip placement="top"
          title={tooltip}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: "14px",
              },
            },
          }}
        >
          {content}
        </Tooltip>
      ) : content;
    },
    [choices, percentage]
  );

  const renderActionCell = useCallback(
    (row: any) => {
      const isFinalizing = row.status === AnalysisStatus.COMPLETED && !row.is_fully_completed;
      const isProcessingOrWaiting = row.status === AnalysisStatus.PROCESSING || row.status === AnalysisStatus.WAITING;
      const isDisabled = isFinalizing || isProcessingOrWaiting;

      return (
        <>
          <Tooltip placement="top"
            title={translate.t("analysis_details")}
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: "14px",
                },
              },
            }}
          >
            <span>
              <IconButton
                sx={{ color: "#90A4AE" }}
                size={"small"}
                disabled={isDisabled}
                onClick={() => handleOpenAnalysisProcessingModal(row)}
              >
                <Info sx={{ fontSize: "24px" }}></Info>
              </IconButton>
            </span>
          </Tooltip>
        </>
      );
    },
    [handleOpenAnalysisProcessingModal, handleOpenDeleteModal, deletingIds]
  );

  const renderCheckboxCell = useCallback(
    (row: Analysis) => {
      const isFinalizing = row.status === AnalysisStatus.COMPLETED && !row.is_fully_completed;
      const isDisabled =
        row.status === AnalysisStatus.WAITING ||
        row.status === AnalysisStatus.PROCESSING ||
        isFinalizing;

      return (
        <Box display="flex" sx={{ height: "100%" }}>
          <Checkbox
            disabled={isDisabled}
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelectOne(row.id)}
            color="primary"
            sx={{
              padding: 0.5,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          />
        </Box>
      );
    },
    [selectedIds, toggleSelectOne]
  );

  const renderHeaderCheckbox = useCallback(
    () => (
      <Box display="flex" sx={{ height: "100%" }}>
        <Checkbox
          indeterminate={
            selectedIds.length > 0 && selectedIds.length < dataSource.length
          }
          checked={selectedIds.length === dataSource.length && dataSource.length > 0}
          onChange={toggleSelectAll}
          sx={{
            padding: 0.5,
            "& .MuiSvgIcon-root": { fontSize: 20 },
            color: "white",
            "& .MuiSvgIcon-root path": {
              stroke: "white",
            },
            "&.Mui-checked": {
              color: "white",
            },
            "&.MuiCheckbox-indeterminate": {
              color: "white",
            },
            "& .MuiTouchRipple-root": {
              color: "white",
            },
          }}
        />
      </Box>
    ),
    [selectedIds, dataSource, toggleSelectAll]
  );

  const renderCustomerCell = useCallback((row: any) => {
    const customer = (row.customer || "").toUpperCase();
    return (
      <span title={customer} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {customer}
      </span>
    );
  }, []);

  const renderDescriptionCell = useCallback((row: any) => formatAnalysisDescription(row), []);

  const renderCellHandlerConfig = useMemo(
    () => ({
      select: (_: string, __: number, row: any) => renderCheckboxCell(row),
      created_at: (_: string, __: number, row: any) => renderCreatedAtCell(row),
      customer: (_: string, __: number, row: any) => renderCustomerCell(row),
      description: (_: string, __: number, row: any) => renderDescriptionCell(row),
      parent_analysis_id: (_: string, __: number, row: any) => renderTypeCell(row),
      status: (_: string, __: number, row: any) => renderStatusCell(row),
      action: (_: string, __: number, row: any) => renderActionCell(row),
    }),
    [
      renderCheckboxCell,
      renderCreatedAtCell,
      renderCustomerCell,
      renderDescriptionCell,
      renderTypeCell,
      renderStatusCell,
      renderActionCell,
    ]
  );

  const renderSelectedAnalyses = useCallback(() => {
    const analyses = dataSource.filter((a) => selectedIds.includes(a.id));

    if (analyses.length === 0) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "10px",
          border: "1px solid #E0E0E0",
          backgroundColor: "#FAFAFA",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 300,
          overflowY: "overlay",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {analyses.map((analysis: any, idx: number) => {
          const showInfoIcon = analysis?.original && analysis?.hasChildren;
          const isChild = !!analysis.parent_analysis_id;

          return (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: "40px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#212121",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  width: "150px",
                  borderRight: "1px solid #E0E0E0",
                  paddingRight: "12px",
                  flexShrink: 0,
                }}
              >
                {analysis.created_at
                  ? dayjs(analysis.created_at).format("DD/MM/YYYY HH:mm")
                  : ""}
              </Typography>

              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#212121",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingLeft: "12px",
                }}
              >
                {analysis?.name ||
                  analysis?.description ||
                  analysis?.title ||
                  `Análise ${analysis?.id}`}
              </Typography>

              {isChild && (
                <Tooltip placement="top"
                  title={translate.t("derived_analysis_warning")}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: "14px",
                      },
                    },
                  }}
                >
                  <Info
                    sx={{
                      color: "#1976D2",
                      marginLeft: 1,
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}

              {showInfoIcon && (
                <Tooltip placement="top"
                  title={translate.t("master_analysis_has_children_warning")}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: "14px",
                      },
                    },
                  }}
                >
                  <Info
                    sx={{
                      color: "#FF9800",
                      marginLeft: 1,
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </Paper>
          );
        })}
      </Paper>
    );
  }, [dataSource, selectedIds]);

  const renderWarning = useCallback(() => {
    const analyses = dataSource.filter((a) => selectedIds.includes(a.id));
    if (analyses.length === 0) return null;

    const hasParent = analyses.some((a: any) => a.original && a.hasChildren);
    const hasChild = analyses.some((a: any) => a.parent_analysis_id);

    return (
      <>
        {hasParent && (
          <Box
            sx={{
              mt: 1.5,
              borderRadius: "8px",
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Info sx={{ color: "#FF9800", marginRight: 1.5 }} />
            <Typography sx={{ fontStyle: "italic", fontWeight: 500 }}>
              {translate.t("analysis_has_child_warning")}
            </Typography>
          </Box>
        )}

        {hasChild && (
          <Box
            sx={{
              mt: 1.5,
              borderRadius: "8px",
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Info sx={{ color: "#1976D2", marginRight: 1.5 }} />
            <Typography sx={{ fontStyle: "italic", fontWeight: 500 }}>
              {translate.t("simulated_analysis")}
            </Typography>
          </Box>
        )}
      </>
    );
  }, [dataSource, selectedIds]);

  return (
    <section>
      <Loading open={isLoading} />
      <div className="floating-title" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Typography variant={"h5"} id="title-analysis">
          {translate.t("analysis")}
        </Typography>
      </div>

      <div className="floating-button">
        <Button
          id="btn-create-analysis"
          component={NavLink}
          to={"create/"}
          startIcon={<Add />}
          variant="contained"
          color="primary"
        >
          {translate.t("create_new_analysis")}
        </Button>
      </div>

      <Grid item xs={12}>
        <Paper variant="outlined">
          <Box p={2}>
            <Formik enableReinitialize={true} initialValues={{ bomNumber: "" }} onSubmit={handleSearchSubmit}>
              {() => (
                <Form autoComplete="off">
                  <Grid alignItems="center" container spacing={2} direction={"row"}>
                    <Grid item xs>
                      <Field
                        id="ip-search-analysis"
                        as={TextField}
                        name="search"
                        label={[translate.t("description")]}
                        fullWidth
                        variant={"outlined"}
                        value={searchFilter}
                        onChange={handleSearchChange}
                      />
                    </Grid>
                    <Grid item>
                      <Fab size="small" type="submit" color="primary" id="btn-search-analysis">
                        <Search />
                      </Fab>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <div>
        {selectedIds.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#FFF8E1",
              border: "1px solid #F8B653",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "16px",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ color: "#FBC02D" }} />
              <Typography sx={{ color: "#6D4C41", fontWeight: 500 }}>
                {selectedIds.length}{" "}
                {selectedIds.length === 1
                  ? translate.t("selected_analysis")
                  : translate.t("selected_analyzes")}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<Delete />}
              sx={{
                backgroundColor: "#3949AB",
                "&:hover": { backgroundColor: "#1F0063" },
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={() => {
                selectedIds.forEach((id) => {
                  const selected = dataSource.find((item) => item.id === id);
                  if (selected) handleOpenDeleteModal(selected);
                });
              }}
            >
              {[translate.t("delete_item")]}
            </Button>
          </Box>
        )}
      </div>

      <Paper
        variant="outlined"
        sx={{
          marginTop: "16px",
          width: "100%",
          "& .MuiTable-root": {
            minWidth: "1254px",
            tableLayout: "fixed",
          },
          "& th:last-child, & td:last-child": {
            width: "70px !important",
            minWidth: "70px !important",
            maxWidth: "70px !important",
          },
        }}
      >
        <TablePaginationComponent
          id="table-analysis"
          tableMinWidth="1254px"
          state={dataSource}
          displayedColumnsShow={DISPLAYED_COLUMNS_SHOW}
          displayedColumns={DISPLAYED_COLUMNS}
          showActions={SHOW_ACTIONS}
          dateColumns={DATE_COLUMNS}
          headerCellCustomStyleHandler={headerCellCustomStyleHandler}
          cellCustomStyleHandler={cellCustomStyleHandler}
          renderCellHandler={renderCellHandlerConfig}
          count={pagination?.count}
          handleDoubleClick={handleRowClick}
          renderHeaderCheckbox={renderHeaderCheckbox}
          headerLabels={{
            id: "ID",
            type: translate.t("type"),
            processing: translate.t("analysis_status"),
          }}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {openDeleteModal && (
        <DialogVersion
          id="dialog-delete-analysis"
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          title={translate.t("delete")}
          message={confirmDeleteMessage}
          labelButton={translate.t("delete")}
          colorButton="error"
        >
          {renderSelectedAnalyses()}
          {renderWarning()}
        </DialogVersion>
      )}

      {openAnalysisProcessingDialog && analysis && (
        <AnalysisProcessingDialog
          open={openAnalysisProcessingDialog}
          onClose={handleCloseAnalysisProcessingDialog}
          analysis={analysis}
          setAnalysis={setAnalysis}
        />
      )}
    </section>
  );
};

export default AnalysisPageComponent;
